/**
 * `/data` — Data Explorer (DATA_VISUALIZATION_FRONTEND_PLAN §2.1).
 *
 * A free date-range explorer over every plottable sensor/DUT/PG&E series, for
 * any role. Source selection + range live in the URL (locked decision: URL is
 * state of record) so a reviewer's working view survives a reload and is
 * shareable. Each series is one parallel `GET /data/series`; a single failing
 * facility surfaces its own error chip without blanking the chart.
 */
import {
  Alert,
  Badge,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconChartLine,
  IconChevronRight,
  IconFileText,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDataCorrections, useDataSeriesMany, useDataSources } from "../api/hooks";
import type { SeriesResponse } from "../api/types";
import { CorrectionModeControl } from "../components/data/CorrectionModeControl";
import { PeriodRangePicker } from "../components/data/PeriodRangePicker";
import { SeriesChart } from "../components/data/SeriesChart";
import { SeriesTable } from "../components/data/SeriesTable";
import { SourcePicker } from "../components/data/SourcePicker";
import { UploadRowsDrawer } from "../components/data/UploadRowsDrawer";
import { colorForIndex } from "../components/data/colors";
import {
  applyApprovedOverlay,
  buildPageOverlays,
  parseCorrMode,
} from "../components/data/correctionOverlay";
import { DEFAULT_SELECTION } from "../components/data/filingSources";
import {
  type Selection,
  decodeSelections,
  encodeSelections,
  selectionKey,
  selectionLabel,
  selectionsToParams,
} from "../components/data/selection";
import {
  type ChartSeriesInput,
  parseChartVariant,
  toSeriesData,
} from "../components/data/series-data";
import { DEMO_MODE, dataVizMswActive } from "../lib/mswEnv";

// Fixture data is centered on Q1 2026; default to the March batch period so
// the explorer opens on a complete, recognizable month.
const DEFAULT_RANGE = { start: "2026-03-01", end: "2026-03-31" };
// The hub outlet meter, volume and energy — the same default the batch Data tab
// opens on, so the two surfaces agree about what "no selection yet" shows.
const DEFAULT_SOURCES = encodeSelections(DEFAULT_SELECTION);

export function DataExplorerPage() {
  const [params, setParams] = useSearchParams();
  const catalogQ = useDataSources();

  // The demo uploads panel renders fixture `demo-*` uploads whose rows only
  // exist behind MSW. Gate on the worker having ACTUALLY started (evaluated at
  // render, after bootstrap) — not just the feature flag — so a production
  // build, dev with VITE_USE_MSW=false, or a worker that failed to register
  // never surfaces sample uploads or fires `/uploads/.../rows` at the backend.
  const demoUploadsEnabled = dataVizMswActive();

  const selections = useMemo(
    () => decodeSelections(params.get("sources") ?? DEFAULT_SOURCES),
    [params],
  );
  const range = {
    start: params.get("start") ?? DEFAULT_RANGE.start,
    end: params.get("end") ?? DEFAULT_RANGE.end,
  };
  const [drillUpload, setDrillUpload] = useState<{ id: string; label: string } | null>(null);

  const setSelections = (next: Selection[]) => {
    const p = new URLSearchParams(params);
    if (next.length === 0) p.set("sources", "");
    else p.set("sources", encodeSelections(next));
    setParams(p, { replace: true });
  };
  const setRange = (r: { start: string; end: string }) => {
    const p = new URLSearchParams(params);
    p.set("start", r.start);
    p.set("end", r.end);
    setParams(p, { replace: true });
  };

  // Granularity (URL state). Hourly only when every selected source supports it
  // (raw-totalizer roles); the settled custody register is daily-only.
  const hourlyRoles = useMemo(
    () =>
      new Set((catalogQ.data?.sources ?? []).filter((s) => s.hourly_capable).map((s) => s.role)),
    [catalogQ.data],
  );
  const hourlyAvailable = selections.length > 0 && selections.every((s) => hourlyRoles.has(s.role));
  const granularity: "daily" | "hourly" =
    params.get("g") === "hourly" && hourlyAvailable ? "hourly" : "daily";
  const setGranularity = (g: string) => {
    const p = new URLSearchParams(params);
    p.set("g", g);
    setParams(p, { replace: true });
  };

  // Chart mode (URL state, like granularity and corrections): line | bar.
  // It lives in the URL rather than component state because reloading or
  // sharing a link must reproduce the view — the same rule the sources, range,
  // granularity and correction mode already follow. Anything unrecognised
  // falls back to line, so a hand-edited link cannot render an empty chart.
  const variant = parseChartVariant(params.get("chart"));
  const setVariant = (v: string) => {
    const p = new URLSearchParams(params);
    p.set("chart", v);
    setParams(p, { replace: true });
  };

  // Corrections display mode (URL state, like granularity): db | approved | all.
  const corrMode = parseCorrMode(params.get("corr"));
  const setCorrMode = (m: string) => {
    const p = new URLSearchParams(params);
    p.set("corr", m);
    setParams(p, { replace: true });
  };
  // Window-scoped: the Explorer has no batch context, so corrections come from
  // GET /data/corrections (any batch overlapping the range).
  const correctionsQ = useDataCorrections(range);

  const seriesQueries = useDataSeriesMany(
    selectionsToParams(selections, range.start, range.end, granularity),
  );

  const loadedSeries: { id: string; label: string; index: number; data: SeriesResponse }[] = [];
  const errors: { key: string; label: string; message: string }[] = [];
  let pending = 0;
  selections.forEach((sel, i) => {
    const q = seriesQueries[i];
    if (!q) return;
    if (q.isError) {
      errors.push({
        key: selectionKey(sel),
        label: selectionLabel(sel, catalogQ.data),
        message: q.error instanceof Error ? q.error.message : "Failed to load",
      });
      return;
    }
    if (q.isPending) {
      pending += 1;
      return;
    }
    if (q.data) {
      loadedSeries.push({
        id: selectionKey(sel),
        label: selectionLabel(sel, catalogQ.data),
        index: i,
        data: q.data,
      });
    }
  });

  // Join corrections onto the loaded series (keyed by the RESPONSE's
  // facility_identifier — see buildPageOverlays). The table gets the RAW
  // points + the overlay map; the chart plots the APPROVED effective values.
  const { index: overlayIndex, overlaysById } = buildPageOverlays(
    correctionsQ.data ?? [],
    granularity,
    loadedSeries.map((r) => ({
      id: r.id,
      facilityIdentifier: r.data.facility_identifier,
      role: r.data.role,
      bucketKeys: r.data.points.map((p) => p.bucket_start),
    })),
  );
  const tableSeries: ChartSeriesInput[] = loadedSeries.map((r) => ({
    id: r.id,
    label: r.label,
    unit: r.data.unit,
    color: colorForIndex(r.index),
    points: toSeriesData(r.data.points, granularity),
    total: r.data.total,
  }));
  const chartSeries: ChartSeriesInput[] = tableSeries.map((s) => ({
    ...s,
    points: applyApprovedOverlay(s.points, overlaysById.get(s.id), corrMode),
  }));
  const overlayActive = corrMode !== "db" && overlaysById.size > 0;
  const unmatchedCount = corrMode !== "db" ? (overlayIndex?.unmatched.length ?? 0) : 0;

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Group gap="sm" align="center">
          <ThemeIcon variant="light" color="indigo" size={36} radius="md">
            <IconChartLine size={20} />
          </ThemeIcon>
          <Title order={2}>Data Explorer</Title>
        </Group>
        <Text c="dimmed" size="sm" maw={760}>
          Visualize any uploaded or sensor series over a custom Pacific gas-day range. Add hub,
          custody, and per-dairy sources, compare them on shared axes, and export the daily values.
          Figures are the backend's exact decimal strings — the chart never recomputes a compliance
          number.
        </Text>
      </Stack>

      {catalogQ.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          Couldn't load the source catalog. Series selection is unavailable.
        </Alert>
      ) : null}

      <Paper p="md" withBorder>
        <Stack gap="md">
          <SourcePicker
            catalog={catalogQ.data}
            value={selections}
            onChange={setSelections}
            loading={catalogQ.isPending}
          />
          <PeriodRangePicker value={range} onChange={setRange} />
        </Stack>
      </Paper>

      {errors.length > 0 ? (
        <Group gap="xs" wrap="wrap">
          {errors.map((e) => (
            <Badge
              key={e.key}
              color="red"
              variant="light"
              leftSection={<IconAlertTriangle size={12} />}
            >
              {e.label}: {e.message}
            </Badge>
          ))}
        </Group>
      ) : null}

      <Paper p="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Group gap="sm">
            <Text size="sm" fw={600}>
              {granularity === "hourly" ? "Hourly series" : "Daily series"}
            </Text>
            {pending > 0 ? (
              <Group gap={6}>
                <Loader size="xs" />
                <Text size="xs" c="dimmed">
                  loading {pending}…
                </Text>
              </Group>
            ) : null}
          </Group>
          <Group gap="sm">
            <Tooltip
              label="Hourly is available only when every selected source supports it. The settled custody energy is one figure per gas day, so a selection including it stays daily."
              disabled={hourlyAvailable}
              multiline
              w={260}
              withArrow
            >
              <SegmentedControl
                size="xs"
                value={granularity}
                onChange={setGranularity}
                data={[
                  { value: "daily", label: "Daily" },
                  { value: "hourly", label: "Hourly", disabled: !hourlyAvailable },
                ]}
              />
            </Tooltip>
            <SegmentedControl
              size="xs"
              value={variant}
              onChange={setVariant}
              data={[
                { value: "line", label: "Line" },
                { value: "bar", label: "Bar" },
              ]}
            />
            <CorrectionModeControl value={corrMode} onChange={setCorrMode} />
          </Group>
        </Group>
        <SeriesChart
          series={chartSeries}
          variant={variant}
          range={range}
          granularity={granularity}
        />
        {overlayActive ? (
          <Text size="xs" c="dimmed" mt="xs">
            Chart shows approved-corrected values; pending proposals appear only in the table.
          </Text>
        ) : null}
      </Paper>

      {unmatchedCount > 0 ? (
        <Text size="xs" c="dimmed">
          {unmatchedCount} correction{unmatchedCount === 1 ? "" : "s"} in this window target upload
          rows or free-text sources and aren't shown in this series view.
        </Text>
      ) : null}
      <SeriesTable
        series={tableSeries}
        filename={`data-explorer_${range.start}_${range.end}.csv`}
        range={range}
        granularity={granularity}
        corrections={{ mode: corrMode, overlays: overlaysById }}
      />

      {demoUploadsEnabled ? (
        <>
          <UploadsPanel onOpen={(id, label) => setDrillUpload({ id, label })} />

          <UploadRowsDrawer
            uploadId={drillUpload?.id ?? null}
            title={drillUpload?.label}
            opened={drillUpload !== null}
            onClose={() => setDrillUpload(null)}
          />
        </>
      ) : null}
    </Stack>
  );
}

/**
 * Sample parsed uploads. A live `/uploads?period=` listing needs the uploads
 * backend; until then these demo rows exercise the rows drill-in + the
 * chart-when-chartable path against MSW.
 */
type DemoUpload = { upload_id: string; type: string; label: string };

function UploadsPanel({ onOpen }: { onOpen: (id: string, label: string) => void }) {
  // Loaded on demand, never statically: `api/dataMock` mirrors the backend
  // catalog with the reference customer's facility identifiers, and a static
  // import shipped all of them in the production bundle. This panel renders
  // only under MSW, so the chunk is only fetched there.
  const [demoUploads, setDemoUploads] = useState<DemoUpload[]>([]);
  useEffect(() => {
    // Never in demo mode: the sample rows carry the reference customer's names,
    // and skipping the import lets the bundler drop the chunk from that build.
    if (DEMO_MODE) return;
    let cancelled = false;
    void import("../api/dataMock").then((m) => {
      if (!cancelled) setDemoUploads(m.DEMO_UPLOADS);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <IconFileText size={18} />
          <Text size="sm" fw={600}>
            Parsed uploads
          </Text>
        </Group>
        <Badge variant="light" color="gray" size="sm">
          sample
        </Badge>
      </Group>
      <Stack gap={6}>
        {demoUploads.map((u) => (
          <UnstyledButton
            key={u.upload_id}
            onClick={() => onOpen(u.upload_id, u.label)}
            style={(t) => ({
              padding: t.spacing.xs,
              borderRadius: t.radius.sm,
              border: `1px solid ${t.colors.gray[2]}`,
            })}
          >
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={0}>
                <Text size="sm">{u.label}</Text>
                <Text size="xs" c="dimmed" ff="monospace">
                  {u.upload_id}
                </Text>
              </Stack>
              <IconChevronRight size={16} />
            </Group>
          </UnstyledButton>
        ))}
      </Stack>
      <Text size="xs" c="dimmed" mt="xs">
        Click an upload to inspect its parsed rows; chartable types (date + numeric columns) offer a
        Chart view.
      </Text>
    </Paper>
  );
}
